import { Card, Typography, Paper } from '@material-ui/core'
import React from 'react'
import styled from 'styled-components'
import { NoteEntityTypeEnum } from '../__generated__/NoteTabQuery.graphql'
import { graphql } from 'babel-plugin-relay/macro'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { useDateTimeDisplay } from '../hooks/timeZoneDisplay'
import { SharedNoteListQuery } from '../__generated__/SharedNoteListQuery.graphql'
import { Person as PersonIcon, AccessTime as TimeIcon } from '@material-ui/icons'

interface SharedNoteListProps {
  entitySlug: string
  entityType: NoteEntityTypeEnum
}

const Root = styled(Paper)`
  padding: 16px !important;
  margin-bottom: 16px !important;
  background-color: white !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
`

const TitleContainer = styled.div`
  display: flex !important;
  align-items: center !important;
  margin-bottom: 16px !important;
`

const Title = styled(Typography)`
  font-weight: 600 !important;
  color: #333 !important;
  font-size: 1.25rem !important;
`

const NotesContainer = styled.div`
  display: flex !important;
  flex-direction: column !important;
  gap: 12px !important;
`

const NoteCard = styled(Card)`
  padding: 16px !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  background-color: white !important;
  transition: all 0.2s ease-in-out !important;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    border-color: #1976d2 !important;
  }
`

const NoteContent = styled(Typography)`
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  line-height: 1.5 !important;
  margin-bottom: 12px !important;
  color: #333 !important;
  font-size: 0.875rem !important;
`

const NoteFooter = styled.div`
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding-top: 8px !important;
  border-top: 1px solid #e0e0e0 !important;
`

const AuthorInfo = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
`

const AuthorIcon = styled(PersonIcon)`
  font-size: 16px !important;
  color: #666 !important;
`

const AuthorText = styled(Typography)`
  font-size: 0.875rem !important;
  color: #666 !important;
  font-weight: 500 !important;
`

const TimeInfo = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
`

const TimeIconStyled = styled(TimeIcon)`
  font-size: 14px !important;
  color: #666 !important;
`

const TimeText = styled(Typography)`
  font-size: 0.75rem !important;
  color: #666 !important;
`

const EmptyState = styled.div`
  text-align: center !important;
  padding: 32px 16px !important;
  color: #666 !important;
`

const EmptyStateText = styled(Typography)`
  color: #666 !important;
  font-style: italic !important;
`

const SharedNoteList: React.FC<SharedNoteListProps> & { Skeleton: React.FC } = ({ entityType, entitySlug }) => {
  const data = useLazyLoadQuery<SharedNoteListQuery>(query, { entityType: entityType, entitySlug: entitySlug })

  const hasNotes = data.externalSharedNoteList.length > 0

  return (
    <Root elevation={0}>
      <TitleContainer>
        <Title variant='h6'>Notes shared with you</Title>
      </TitleContainer>

      {hasNotes ? (
        <NotesContainer>
          {data.externalSharedNoteList.map(
            (note) =>
              note && (
                <NoteCard key={note.slug} variant='outlined'>
                  <NoteContent variant='body1'>{note.content}</NoteContent>
                  <NoteFooter>
                    <AuthorInfo>
                      <AuthorIcon />
                      <AuthorText variant='caption'>{note.addedBy.username}</AuthorText>
                    </AuthorInfo>
                    <TimeInfo>
                      <TimeIconStyled />
                      <TimeText variant='caption'>{useDateTimeDisplay(note.addedAt)}</TimeText>
                    </TimeInfo>
                  </NoteFooter>
                </NoteCard>
              )
          )}
        </NotesContainer>
      ) : (
        <EmptyState>
          <EmptyStateText variant='body2'>No notes have been shared with you yet.</EmptyStateText>
        </EmptyState>
      )}
    </Root>
  )
}

const query = graphql`
  query SharedNoteListQuery($entityType: NoteEntityTypeEnum!, $entitySlug: String!) {
    externalSharedNoteList(entityType: $entityType, entitySlug: $entitySlug) {
      slug
      content
      addedAt
      addedBy {
        username
      }
    }
  }
`

SharedNoteList.Skeleton = () => (
  <Root elevation={0}>
    <TitleContainer>
      <Title variant='h6'>Notes shared with you</Title>
    </TitleContainer>
    <EmptyState>
      <EmptyStateText variant='body2'>Loading...</EmptyStateText>
    </EmptyState>
  </Root>
)

export { SharedNoteList }
